// useResponsive: 响应式断点检测
// 同步 CSS media queries (保持一致)

import { onBeforeUnmount, onMounted, ref } from 'vue'

export type Breakpoint = 'mobile' | 'tablet' | 'desktop'

const QUERIES: Record<Breakpoint, string> = {
  mobile: '(max-width: 768px)',
  tablet: '(min-width: 769px) and (max-width: 1024px)',
  desktop: '(min-width: 1025px)',
}

const mqls: Record<Breakpoint, MediaQueryList | null> = {
  mobile: null,
  tablet: null,
  desktop: null,
}

const current = ref<Breakpoint>('desktop')

let listeners: Array<() => void> = []
let consumers = 0

function updateCurrent() {
  for (const bp of ['mobile', 'tablet', 'desktop'] as Breakpoint[]) {
    if (mqls[bp]?.matches) {
      current.value = bp
      break
    }
  }
}

function setup() {
  if (typeof window === 'undefined') return

  for (const bp of Object.keys(QUERIES) as Breakpoint[]) {
    const mql = window.matchMedia(QUERIES[bp])
    mqls[bp] = mql
    mql.addEventListener('change', updateCurrent)
    listeners.push(() => mql.removeEventListener('change', updateCurrent))
  }
  updateCurrent()
}

function teardown() {
  listeners.forEach((fn) => fn())
  listeners = []
  for (const bp of Object.keys(mqls) as Breakpoint[]) mqls[bp] = null
}

export function useResponsive() {
  // setup/teardown 只操作模块级状态, 提到模块作用域。
  // 多实例下用引用计数: 任一消费者 unmount 都移除全部监听会让其余实例失灵
  // (ALENS-107 #8)。
  onMounted(() => {
    consumers += 1
    if (consumers === 1) setup()
  })
  onBeforeUnmount(() => {
    consumers = Math.max(0, consumers - 1)
    if (consumers === 0) teardown()
  })

  return {
    current,
    isMobile: () => current.value === 'mobile',
    isTablet: () => current.value === 'tablet',
    isDesktop: () => current.value === 'desktop',
  }
}
